import { useCallback } from 'react';
import { NodeType } from '../types/cytoscape';
import { atom, useAtom } from 'jotai';

// グローバルなハイライト状態を管理するための Jotai アトム
const highlightedNodesAtom = atom<string[]>([]);

// useNodeHighlighting.ts - ハイライトのみ担当
export const useNodeHighlighting = () => {
  const [highlightedNodes, setHighlightedNodes] = useAtom(highlightedNodesAtom);

  const applyHighlighting = (
    cytoscapeCoreRefObject: cytoscape.Core | null,
    nodeId: string,
    className: string = 'highlighted'
  ) => {
    const cy = cytoscapeCoreRefObject;
    if (!cy || !nodeId) return;

    // Clear previous highlighting classes
    cy.elements().removeClass(className);
    cy.elements().removeClass('faded');

    // Apply highlighting to the node and its connections
    const targetNode = cy.$id(nodeId);
    if (targetNode.length === 0) return;

    targetNode.addClass(className);
    targetNode.neighborhood().addClass(className);

    // neighborhood nodes
    const neighborhoodNodes = targetNode
      .neighborhood()
      .filter((ele) => ele.isNode()) as cytoscape.NodeCollection;

    // 新しい配列をセット（prevを使わない）
    setHighlightedNodes([nodeId, ...neighborhoodNodes.map((n) => n.id())]);

    if (targetNode.isNode() && targetNode.data('nodeType') === NodeType.THEME) {
      targetNode.connectedEdges().addClass('theme-highlighted');
    } else {
      targetNode.connectedEdges().addClass(className);
    }

    // Fade non-highlighted elements
    cy.elements().not(`.${className}`).addClass('faded');
    cy.elements()
      .filter((ele) => ele.hasClass('searched'))
      .removeClass('faded');
  };

  const removeHighlighting = useCallback(
    (cytoscapeCoreRefObject: cytoscape.Core | null) => {
      const cy = cytoscapeCoreRefObject;
      if (!cy) return;

      cy.elements().removeClass('highlighted faded theme-highlighted');

      setHighlightedNodes([]); // 空配列によりハイライト状態ノード配列の記録をリセット
    },
    []
  );

  const highlightNodeAndConnections = useCallback(
    (cy: cytoscape.Core | null, nodeId: string) => {
      if (!cy) return;

      if (highlightedNodes.includes(nodeId)) {
        // 既にハイライトされている場合は ハイライトを解除
        removeHighlighting(cy);
      } else {
        // ハイライト処理
        applyHighlighting(cy, nodeId);
      }
    },
    [highlightedNodes, removeHighlighting, applyHighlighting]
  );

  return {
    highlightedNodes,
    highlightNodeAndConnections,
    removeHighlighting,
  };
};
